import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { AppDispatch, RootState } from 'store'
import { TabsContainer, Tabs } from 'styling/GlobalStyles'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { ChartCard } from '../components/ChartCard'
import { ChartTooltip } from '../components/ChartTooltip'
import { AnalyticsTable, RateCellRenderer } from '../components/AnalyticsTable'
import { KpiRow } from '../HrAnalytics.styled'
import {
  clearHrAnalytics,
  getLogisticsAccommodationStatsByCamp,
  getLogisticsAccommodationSummary,
  getLogisticsFoodStatsByDay,
  getLogisticsFoodStatsByMeal,
  getLogisticsFoodStatsByWeek,
  getLogisticsFoodSummary,
} from 'features/hrAnalytics/hrAnalytics.slice'
import { setTimeTo } from 'utils/helpers'
import { tokens } from 'styling/tokens'

const KpiIcons: Record<string, React.ReactNode> = {
  food: <svg viewBox='0 0 24 24'><path d='M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 000 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
  chart: <svg viewBox='0 0 24 24'><path d='M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z' /></svg>,
  bed: <svg viewBox='0 0 24 24'><path d='M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2V10c0-2.21-1.79-4-4-4z' /></svg>,
  person: <svg viewBox='0 0 24 24'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' /></svg>,
}

const LOGISTICS_SUB_TABS = [
  { key: 'food', label: 'Food', path: '/hr/analytics/logistics/food' },
  { key: 'accommodation', label: 'Accommodation', path: '/hr/analytics/logistics/accommodation' },
]

const getSubTab = (pathname: string): string => {
  if (pathname.includes('/accommodation')) return 'accommodation'
  return 'food'
}

const FOOD_COLUMNS = [
  { key: 'mealType', label: 'Meal Type', sortable: true, width: '25%' },
  { key: 'planned', label: 'Planned', sortable: true, width: '25%' },
  { key: 'served', label: 'Served', sortable: true, width: '25%' },
  {
    key: 'utilizationRate',
    label: 'Utilization Rate',
    sortable: true,
    width: '25%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const CAMP_COLUMNS = [
  { key: 'campName', label: 'Camp Name', sortable: true, width: '20%' },
  { key: 'totalBeds', label: 'Total Beds', sortable: true, width: '18%' },
  { key: 'avgOccupied', label: 'Avg Occupied', sortable: true, width: '18%' },
  { key: 'avgAvailable', label: 'Avg Available', sortable: true, width: '18%' },
  {
    key: 'occupancyRate',
    label: 'Occupancy Rate',
    sortable: true,
    width: '26%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const formatShortDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const formatWeekLabel = (iso: string) => {
  const d = new Date(iso)
  return `W/C ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

const formatPercent = (value: number) => `${(value ?? 0).toFixed(1)}%`


const FoodSubTab = () => {
  const [period, setPeriod] = useState('Day')

  const {
    logisticsFoodSummary,
    logisticsFoodStatsByDay,
    logisticsFoodStatsByWeek,
    logisticsFoodStatsByMeal,
  } = useSelector((state: RootState) => state.hrAnalytics)

  const kpis = useMemo(
    () => [
      {
        value: (logisticsFoodSummary?.mealsPlanned ?? 0).toLocaleString(),
        label: 'MEALS PLANNED',
        accentColor: tokens.status.info.fg,
        iconKey: 'food',
      },
      {
        value: (logisticsFoodSummary?.mealsServed ?? 0).toLocaleString(),
        label: 'MEALS SERVED',
        accentColor: tokens.status.success.fg,
        iconKey: 'checkCircle',
      },
      {
        value: formatPercent(logisticsFoodSummary?.wasteIndicatorPercent ?? 0),
        label: 'WASTE INDICATOR',
        accentColor: tokens.status.danger.fg,
        iconKey: 'chart',
      },
    ],
    [logisticsFoodSummary],
  )

  const chartData = useMemo(() => {
    if (period === 'Week') {
      return logisticsFoodStatsByWeek.map((w) => ({
        label: formatWeekLabel(w.weekStartDate),
        planned: w.mealsPlanned,
        served: w.mealsServed,
      }))
    }
    return logisticsFoodStatsByDay.map((d) => ({
      label: formatShortDate(d.date),
      planned: d.mealsPlanned,
      served: d.mealsServed,
    }))
  }, [period, logisticsFoodStatsByDay, logisticsFoodStatsByWeek])

  const mealRows = useMemo(
    () =>
      logisticsFoodStatsByMeal.map((m) => ({
        mealType: m.mealTypeName,
        planned: m.mealsPlanned,
        served: m.mealsServed,
        utilizationRate: m.utilizationRatePercent,
      })),
    [logisticsFoodStatsByMeal],
  )

  return (
    <>
      <KpiRow>
        {kpis.map((kpi, idx) => (
          <MetricCard
            key={idx}
            layout='inline'
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Planned vs Served'
        periods={['Day', 'Week']}
        activePeriod={period}
        onPeriodChange={setPeriod}
        legend={[
          { label: 'Planned', color: tokens.accent.base },
          { label: 'Served', color: tokens.status.info.fg },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
            <XAxis dataKey='label' tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <Tooltip cursor={{ fill: tokens.surface.inset }} content={<ChartTooltip />} />
            <Bar dataKey='planned' name='Planned' fill={tokens.accent.base} radius={[4, 4, 0, 0]} barSize={20} />
            <Bar dataKey='served' name='Served' fill={tokens.status.info.fg} radius={[4, 4, 0, 0]} barSize={20} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <AnalyticsTable columns={FOOD_COLUMNS} data={mealRows} />
    </>
  )
}



const AccommodationSubTab = () => {
  const {
    logisticsAccommodationSummary,
    logisticsAccommodationStatsByCamp,
  } = useSelector((state: RootState) => state.hrAnalytics)

  const kpis = useMemo(
    () => [
      {
        value: (logisticsAccommodationSummary?.totalBeds ?? 0).toLocaleString(),
        label: 'TOTAL BEDS',
        accentColor: tokens.status.info.fg,
        iconKey: 'bed',
      },
      {
        value: Math.round(logisticsAccommodationSummary?.averageOccupancy ?? 0).toLocaleString(),
        label: 'AVERAGE OCCUPANCY',
        sublabel: formatPercent(logisticsAccommodationSummary?.occupancyRatePercent ?? 0),
        accentColor: tokens.status.success.fg,
        iconKey: 'checkCircle',
      },
      {
        value: (logisticsAccommodationSummary?.guestStays ?? 0).toLocaleString(),
        label: 'GUEST STAYS',
        sublabel: 'Non-Employee Stays',
        accentColor: tokens.status.info.fg,
        iconKey: 'person',
      },
    ],
    [logisticsAccommodationSummary],
  )

  const campChartData = useMemo(
    () =>
      logisticsAccommodationStatsByCamp.map((c) => ({
        campName: c.campName,
        avgOccupied: Math.round(c.averageOccupiedBeds),
        totalBeds: c.totalBeds,
      })),
    [logisticsAccommodationStatsByCamp],
  )

  const campRows = useMemo(
    () =>
      logisticsAccommodationStatsByCamp.map((c) => ({
        campName: c.campName,
        totalBeds: c.totalBeds,
        avgOccupied: Math.round(c.averageOccupiedBeds),
        avgAvailable: Math.round(c.averageAvailableBeds),
        occupancyRate: c.occupancyRatePercent,
      })),
    [logisticsAccommodationStatsByCamp],
  )

  return (
    <>
      <KpiRow>
        {kpis.map((kpi, idx) => (
          <MetricCard
            key={idx}
            layout='inline'
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Average Occupied Beds by Camp'
        legend={[
          { label: 'Avg Occupied', color: tokens.status.info.fg },
          { label: 'Total Beds', color: tokens.border.hairline },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart data={campChartData}>
            <CartesianGrid strokeDasharray='3 3' stroke={tokens.border.hairline} />
            <XAxis dataKey='campName' tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: tokens.text.muted }} tickLine={false} />
            <Tooltip cursor={{ fill: tokens.surface.inset }} content={<ChartTooltip />} />
            <Bar dataKey='totalBeds' name='Total Beds' fill={tokens.border.hairline} radius={[4, 4, 0, 0]} barSize={24} />
            <Bar dataKey='avgOccupied' name='Avg Occupied' fill={tokens.status.info.fg} radius={[4, 4, 0, 0]} barSize={24} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <AnalyticsTable columns={CAMP_COLUMNS} data={campRows} />
    </>
  )
}

export const LogisticsTab = ({ dateFrom, dateTo }: ITab) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const activeSubTab = getSubTab(location.pathname)
  const startTs = dateFrom?.getTime()
  const endTs = dateTo?.getTime()

  useEffect(() => {
    if (!startTs || !endTs) return
    const params = {
      startDate: setTimeTo(new Date(startTs), 'start'),
      endDate: setTimeTo(new Date(endTs), 'end'),
    }
    dispatch(getLogisticsFoodSummary({ ...params }))
    dispatch(getLogisticsFoodStatsByDay({ ...params }))
    dispatch(getLogisticsFoodStatsByWeek({ ...params }))
    dispatch(getLogisticsFoodStatsByMeal({ ...params }))
    dispatch(getLogisticsAccommodationSummary({ ...params }))
    dispatch(getLogisticsAccommodationStatsByCamp({ ...params }))
  }, [startTs, endTs])

  useEffect(() => () => {
    dispatch(clearHrAnalytics())
  }, [])

  return (
    <>
      <TabsContainer>
        {LOGISTICS_SUB_TABS.map((tab) => (
          <Tabs
            key={tab.key}
            active={activeSubTab === tab.key}
            onClick={() => navigate(tab.path)}
          >
            {tab.label}
          </Tabs>
        ))}
      </TabsContainer>

      {activeSubTab === 'food' ? <FoodSubTab /> : <AccommodationSubTab />}
    </>
  )
}


interface ITab {
  dateFrom: Date
  dateTo: Date
}

